import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getChargingSite,
  updateChargingSite,
  getSiteOpeningHours,
} from "../../api/ChargingSites";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import ConfirmationModal from "../../components/ConfirmationModal";
import SiteForm from "../../components/site/SiteForm";
import SiteGridSettings from "../../components/gridCost/SiteGridSettings";
import SiteThirdPartyRef from "../../components/site/SiteThirdPartyRef";
import LocationData from "../../components/site/LocationData";
import ChargingSiteStatusLog from "../../components/site/ChargingSiteStatusLog";
import SiteHierarchy from "../../components/site/SiteHierarchy";
import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";
import {
  getAllAccessGroups,
  getSimpleAccessGroupsByOperatorOrganizationPublicId,
} from "../../api/AccessGroups";
import {
  getCustomerDisplayName,
  shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import PointSmartCharging from "../../components/site/PointSmartCharging";
import SiteSmartCharging from "../../components/site/SiteSmartCharging";
import { Roles } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";

const DEFAULT_TAB = "hierarchy";

const INITIAL_CHARGING_SITE = {
  name: "",
  address: "",
  city: "",
  zipCode: "",
  latitude: "",
  longitude: "",
  chargingSitePublicId: "",
  chargingPoints: [],
  chargingWhenClosed: false,
  isNotificationEnabled: false,
  timeZone: "",
};

const INITIAL_HEAVY_VEHICLE = {
  suitability: "TRUCK_TRAILER",
  trailer_parking: false,
  truck_only: false,
  height_restriction: false,
  height: "0",
  width: "0",
  length: "0",
  mass: "0",
  cable_side: [],
  drive_through: false,
  dangerous_goods: false,
  rigid_vehicles: false,
  overnight_parking: false,
  amenities: [],
  barrier: false,
  cable_length: "0",
  security_measures: [],
  turning_radius: "0",
};

const TAB_ITEMS = [
  { id: "details", labelKey: "tabs.details" },
  { id: "hierarchy", labelKey: "tabs.hierarchy" },
  { id: "events", labelKey: "tabs.events" },
  { id: "data", labelKey: "tabs.locationData" },
  { id: "accessGroups", labelKey: "tabs.accessGroups" },
  { id: "references", labelKey: "tabs.references" },
  { id: "smartCharging", labelKey: "tabs.smartCharging" },
];

const trimOrNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};

const numOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const intOrNull = (v) => {
  const n = numOrNull(v);
  return n == null ? null : Math.round(n);
};

const listOrNull = (values = []) => {
  const normalized = values
    .map((value) => trimOrNull(value))
    .filter((value) => value != null);

  return normalized.length ? normalized : null;
};

const toParkingSpaceDto = (space = {}) => ({
  parkingPublicId: trimOrNull(space.parkingPublicId),
  physicalReference: trimOrNull(space.physicalReference),
  vehicleTypes: listOrNull(space.vehicleTypes ?? []),
  restrictedToType: !!space.restrictedToType,
  reservationRequired: !!space.reservationRequired,
  maxVehicleWeight: numOrNull(space.maxVehicleWeight),
  maxVehicleHeight: numOrNull(space.maxVehicleHeight),
  maxVehicleLength: numOrNull(space.maxVehicleLength),
  maxVehicleWidth: numOrNull(space.maxVehicleWidth),
  parkingSpaceLength: numOrNull(space.parkingSpaceLength),
  parkingSpaceWidth: numOrNull(space.parkingSpaceWidth),
  dangerousGoodsAllowed: space.dangerousGoodsAllowed ?? null,
  direction: trimOrNull(space.direction),
  driveThrough: space.driveThrough ?? null,
  timeLimit: intOrNull(space.timeLimit),
  roofed: space.roofed ?? null,
  lighting: space.lighting ?? null,
  refrigerationOutlet: space.refrigerationOutlet ?? null,
  standards: listOrNull(space.standards ?? []),
  apdsReference: trimOrNull(space.apdsReference),
});

const toRelatedLocationDto = (location = {}) => ({
  relatedLocationPublicId: trimOrNull(location.relatedLocationPublicId),
  latitude: String(location.latitude ?? "").trim(),
  longitude: String(location.longitude ?? "").trim(),
  language: trimOrNull(location.language),
  text: trimOrNull(location.text),
});


const toHeavyVehicleDto = (hv = {}) => ({
  suitability: trimOrNull(hv.suitability) ?? "TRUCK_TRAILER",
  trailer_parking: !!hv.trailer_parking, // bool (ej nullable)
  truck_only: !!hv.truck_only, // bool (ej nullable)
  height_restriction: hv.height_restriction ?? null, // bool?
  height: numOrNull(hv.height), // double?
  width: numOrNull(hv.width), // double?
  length: numOrNull(hv.length), // double?
  mass: numOrNull(hv.mass), // double?
  cable_side: Array.isArray(hv.cable_side) ? hv.cable_side.map(String) : null, // string[]?
  drive_through: hv.drive_through ?? null, // bool?
  dangerous_goods: hv.dangerous_goods ?? null, // bool?
  rigid_vehicles: hv.rigid_vehicles ?? null, // bool?
  overnight_parking: hv.overnight_parking ?? null, // bool?
  amenities: Array.isArray(hv.amenities) ? hv.amenities.map(String) : null, // string[]?
  barrier: hv.barrier ?? null, // bool?
  cable_length: intOrNull(hv.cable_length), // int?
  security_measures: Array.isArray(hv.security_measures)
    ? hv.security_measures.map(String)
    : null, // string[]?
  turning_radius: intOrNull(hv.turning_radius), // int?
});

const toUpdateChargingSiteDto = (site) => ({
  chargingSitePublicId: site?.chargingSitePublicId,
  name: trimOrNull(site?.name),
  address: trimOrNull(site?.address),
  city: trimOrNull(site?.city),
  zipCode: trimOrNull(site?.zipCode),
  latitude: numOrNull(site?.latitude),
  longitude: numOrNull(site?.longitude),
  isEnabled: site?.isEnabled ?? null,
  heavyVehicle: toHeavyVehicleDto(site?.heavyVehicle),
  biddingZonePublicId: trimOrNull(site?.biddingZonePublicId),
  helpPhone: trimOrNull(site?.helpPhone),
  isPublished: site?.isPublished ?? null,
  publishPublicInApp: site?.publishPublicInApp ?? false,
  countryCode: trimOrNull(site?.countryCode) ?? "SE",
  partyId: site?.partyId ?? "GTO",
  allowedCustomers: Array.isArray(site?.allowedCustomers)
    ? site.allowedCustomers
    : null,
  parkingType: trimOrNull(site?.parkingType),
  chargingWhenClosed: site?.chargingWhenClosed ?? null,
  isNotificationEnabled: site?.isNotificationEnabled ?? null,
  timeZone: trimOrNull(site?.timeZone),
  operatorPublicId: trimOrNull(site?.operatorBusinessPublicId),
  subOperatorPublicId: trimOrNull(site?.subOperatorBusinessPublicId),
  ownerPublicId: trimOrNull(site?.ownerBusinessPublicId),
  energyMixPublicId: trimOrNull(site?.energyMixPublicId),
  parkingSpaces: Array.isArray(site?.parkingSpaces)
    ? site.parkingSpaces.map(toParkingSpaceDto)
    : [],
  relatedLocations: Array.isArray(site?.relatedLocations)
    ? site.relatedLocations.map(toRelatedLocationDto)
    : [],
  gridAreaPublicId: trimOrNull(site?.gridAreaPublicId),
  connectionKw: numOrNull(site?.connectionKw),
  gridVoltageLevel:
    site?.gridVoltageLevel === "" || site?.gridVoltageLevel == null
      ? null
      : trimOrNull(site?.gridVoltageLevel),
});

const getTabButtonClassName = (activeTab, tabId) =>
  `px-4 pb-2 ${
    activeTab === tabId
      ? "border-b-2 border-black font-bold text-sm text-black"
      : "text-mute-400 text-sm font-bold"
  }`;

const mapAccessGroupOption = (accessGroup, includeOrganization = false) => {
  const name = accessGroup?.name ?? accessGroup?.Name ?? "";
  return {
    ...accessGroup,
    accessGroupPublicId: accessGroup.accessGroupPublicId,
    name,
    displayName: getCustomerDisplayName(
      { ...accessGroup, name },
      includeOrganization
    ),
  };
};

const mergeAccessGroupOptions = (includeOrganization, ...lists) => {
  const byId = new Map();
  lists.flat().forEach((accessGroup) => {
    if (accessGroup?.accessGroupPublicId) {
      byId.set(
        accessGroup.accessGroupPublicId,
        mapAccessGroupOption(accessGroup, includeOrganization)
      );
    }
  });
  return Array.from(byId.values());
};

const EditSitePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(location.search).get("tab") ?? DEFAULT_TAB
  );
  const { getAccessToken, hasRole } = useAuth();
  const isAdmin = hasRole?.(Roles.Admin) ?? false;
  const includeOrganization = shouldShowOrganizationName(hasRole);
  const { id } = useParams();
  const navigate = useNavigate();

  const [accessGroupPublicIds, setAccessGroupPublicIds] = useState([]);
  const [originalAccessGroupPublicIds, setOriginalAccessGroupPublicIds] = useState([]);
  const [siteAccessGroupItems, setSiteAccessGroupItems] = useState([]);

  const [parties, setParties] = useState([]);
  const [originalParties, setOriginalParties] = useState([]);
  const [parkingSpaces, setParkingSpaces] = useState([]);
  const [originalParkingSpaces, setOriginalParkingSpaces] = useState([]);
  const [invalidParkingVehicleTypesIndices, setInvalidParkingVehicleTypesIndices] =
    useState([]);
  const [
    invalidParkingPhysicalReferenceIndices,
    setInvalidParkingPhysicalReferenceIndices,
  ] = useState([]);
  const [relatedLocations, setRelatedLocations] = useState([]);
  const [originalRelatedLocations, setOriginalRelatedLocations] = useState([]);

  const [chargingSite, setChargingSite] = useState(INITIAL_CHARGING_SITE);
  const [originalChargingSite, setOriginalChargingSite] = useState(null);

  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);
  const [originalThirdPartySystemMapping, setOriginalThirdPartySystemMapping] =
    useState([]);

  const [loadingSite, setLoadingSite] = useState(true);

  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [hasChanges, setHasChanges] = useState(false);
  const [heavyVehicleData, setHeavyVehicleData] = useState(INITIAL_HEAVY_VEHICLE);

  const [openingTimes, setOpeningTimes] = useState(null);

  const handleInputChange = (key, value) => {
    setChargingSite((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const clearParkingVehicleTypesInvalid = (index) => {
    setInvalidParkingVehicleTypesIndices((prev) =>
      prev.filter((i) => i !== index)
    );
  };

  const clearParkingPhysicalReferenceInvalid = (index) => {
    setInvalidParkingPhysicalReferenceIndices((prev) =>
      prev.filter((i) => i !== index)
    );
  };

  useEffect(() => {
    setChargingSite((prev) => ({
      ...prev,
      heavyVehicle: heavyVehicleData,
    }));
    setHasChanges(true);
  }, [heavyVehicleData]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab) {
      if (tab === "references" && !isAdmin) {
        setActiveTab(DEFAULT_TAB);
        return;
      }
      setActiveTab(tab);
    }
  }, [location.search, isAdmin]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [activeTab, location.pathname, navigate]);

  useEffect(() => {
    if (id !== "0") {
      fetchChargingSite();
      fetchOpeningHours();
    } else {
      setLoadingSite(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id]);

  const fetchChargingSite = async () => {
    try {
      const accessToken = await getAccessToken();
      const siteData = await getChargingSite(id, accessToken);
      const normalizedData = {
        name: siteData.name ?? null,
        address: siteData.address ?? null,
        city: siteData.city ?? null,
        zipCode: siteData.zipCode ?? null,
        latitude: siteData.latitude !== null ? siteData.latitude : null,
        longitude: siteData.longitude !== null ? siteData.longitude : null,
        countryCode: siteData.countryCode ?? null,
        countryName: siteData.countryName ?? null,
        chargingSitePublicId: siteData.chargingSitePublicId ?? null,
        heavyVehicle: siteData.heavyVehicle ?? INITIAL_HEAVY_VEHICLE,
        chargingPoints: siteData.chargingPoints ?? [],
        isPublished: siteData.isPublished ?? false,
        publishPublicInApp: siteData.publishPublicInApp ?? false,
        parkingType: siteData.parkingType ?? null,
        chargingWhenClosed: siteData.chargingWhenClosed ?? false,
        isNotificationEnabled: siteData.isNotificationEnabled ?? false,
        timeZone: siteData.timeZone ?? null,
        subOperatorBusinessPublicId:
          siteData.subOperator?.organizationPublicId ?? null,
        operatorBusinessPublicId:
          siteData.operator?.organizationPublicId ?? null,
        ownerBusinessPublicId: siteData.owner?.organizationPublicId ?? null,
        biddingZonePublicId: siteData.biddingZonePublicId ?? null,
        helpPhone: siteData.helpPhone ?? null,
        gridAreaPublicId: siteData.gridAreaPublicId ?? "",
        connectionKw: siteData.connectionKw != null ? String(siteData.connectionKw) : "",
        gridVoltageLevel: siteData.gridVoltageLevel ?? "",
        parkingSpaces: siteData.parkingSpaces ?? [],
        relatedLocations: siteData.relatedLocations ?? [],
        organizations: siteData.organizations ?? [],
      };

      if (siteData.heavyVehicle) {
        setHeavyVehicleData(siteData.heavyVehicle);
      }
      const mappings = siteData.thirdPartyMappings ?? [];
      setThirdPartySystemMapping(mappings);
      setOriginalThirdPartySystemMapping(mappings);
      const siteAccessGroups = Array.isArray(siteData.accessGroups) ? siteData.accessGroups : [];
      const siteAccessGroupIds = siteAccessGroups.map((ag) => ag.accessGroupPublicId);
      setSiteAccessGroupItems(
        siteAccessGroups.map((accessGroup) =>
          mapAccessGroupOption(accessGroup, includeOrganization)
        )
      );
      setAccessGroupPublicIds(siteAccessGroupIds);
      setOriginalAccessGroupPublicIds(siteAccessGroupIds);
      setParties(siteData.remoteParties || []);
      setOriginalParties(siteData.remoteParties || []);
      setParkingSpaces(siteData.parkingSpaces ?? []);
      setOriginalParkingSpaces(siteData.parkingSpaces ?? []);
      setRelatedLocations(siteData.relatedLocations ?? []);
      setOriginalRelatedLocations(siteData.relatedLocations ?? []);
      setChargingSite(normalizedData);
      setOriginalChargingSite(normalizedData);
    } catch (error) {
      toast.error(error?.message || t("toast.failedFetch", { entity: t("entity.chargingSite") }));
    } finally {
      setLoadingSite(false);
    }
  };

  const sortArr = (a = []) =>
    [...a].filter((v) => v !== undefined && v !== null).sort();

  const toNum = (v, def = 0) => Number(v ?? def);

  const normalizeHV = (hv = {}) => ({
    suitability: hv.suitability ?? "TRUCK_TRAILER",
    trailer_parking: !!hv.trailer_parking,
    truck_only: !!hv.truck_only,
    height_restriction: !!hv.height_restriction,
    height: toNum(hv.height),
    width: toNum(hv.width),
    length: toNum(hv.length),
    mass: toNum(hv.mass),
    cable_side: sortArr(hv.cable_side),
    drive_through: !!hv.drive_through,
    dangerous_goods: !!hv.dangerous_goods,
    rigid_vehicles: !!hv.rigid_vehicles,
    overnight_parking: !!hv.overnight_parking,
    amenities: sortArr(hv.amenities),
    barrier: !!hv.barrier,
    cable_length: toNum(hv.cable_length),
    security_measures: sortArr(hv.security_measures),
    turning_radius: toNum(hv.turning_radius),
  });

  const normalizeSite = (site) => ({
    name: site?.name ?? "",
    address: site?.address ?? "",
    city: site?.city ?? "",
    zipCode: site?.zipCode ?? "",
    latitude: site?.latitude ?? "",
    longitude: site?.longitude ?? "",
    countryCode: site?.countryCode ?? "",
    chargingSitePublicId: site?.chargingSitePublicId ?? "",
    isPublished: !!site?.isPublished,
    publishPublicInApp: !!site?.publishPublicInApp,
    parkingType: site?.parkingType ?? "",
    energyMix: site?.energyMix ?? null,
    operatorBusinessPublicId: site?.operatorBusinessPublicId ?? null,
    subOperatorBusinessPublicId: site?.subOperatorBusinessPublicId ?? null,
    ownerBusinessPublicId: site?.ownerBusinessPublicId ?? null,
    chargingWhenClosed: !!site?.chargingWhenClosed,
    isNotificationEnabled: !!site?.isNotificationEnabled,
    heavyVehicle: normalizeHV(site?.heavyVehicle ?? {}),
    timeZone: site?.timeZone ?? "",
    biddingZonePublicId: site?.biddingZonePublicId ?? "",
    siteData: site?.helpPhone ?? "",
    gridAreaPublicId: site?.gridAreaPublicId ?? "",
    connectionKw: site?.connectionKw != null ? String(site.connectionKw) : "",
    gridVoltageLevel: site?.gridVoltageLevel ?? "",
  });

  const normalizeParkingSpaces = (spaces = []) =>
    spaces.map((space) => ({
      parkingPublicId: space?.parkingPublicId ?? "",
      physicalReference: space?.physicalReference ?? "",
      vehicleTypes: [...(space?.vehicleTypes ?? [])].map(String).sort(),
      restrictedToType: !!space?.restrictedToType,
      reservationRequired: !!space?.reservationRequired,
      maxVehicleWeight: numOrNull(space?.maxVehicleWeight),
      maxVehicleHeight: numOrNull(space?.maxVehicleHeight),
      maxVehicleLength: numOrNull(space?.maxVehicleLength),
      maxVehicleWidth: numOrNull(space?.maxVehicleWidth),
      parkingSpaceLength: numOrNull(space?.parkingSpaceLength),
      parkingSpaceWidth: numOrNull(space?.parkingSpaceWidth),
      dangerousGoodsAllowed: space?.dangerousGoodsAllowed ?? null,
      direction: space?.direction ?? "",
      driveThrough: space?.driveThrough ?? null,
      timeLimit: intOrNull(space?.timeLimit),
      roofed: space?.roofed ?? null,
      lighting: space?.lighting ?? null,
      refrigerationOutlet: space?.refrigerationOutlet ?? null,
      standards: [...(space?.standards ?? [])].map(String).sort(),
      apdsReference: space?.apdsReference ?? "",
    }));

  const normalizeRelatedLocations = (locations = []) =>
    locations.map((location) => ({
      relatedLocationPublicId: location?.relatedLocationPublicId ?? "",
      latitude: location?.latitude ?? "",
      longitude: location?.longitude ?? "",
      language: location?.language ?? "",
      text: location?.text ?? "",
    }));

  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  useEffect(() => {
    if (!originalChargingSite) return;

    const siteChanged = !isEqual(
      normalizeSite(chargingSite),
      normalizeSite(originalChargingSite)
    );

    const thirdPartyChanged =
      JSON.stringify(thirdPartySystemMapping) !==
      JSON.stringify(originalThirdPartySystemMapping);

    const accessGroupsChanged =
      JSON.stringify([...accessGroupPublicIds].sort()) !==
      JSON.stringify([...originalAccessGroupPublicIds].sort());

    const partiesChanged =
      JSON.stringify(parties) !== JSON.stringify(originalParties);

    const parkingSpacesChanged = !isEqual(
      normalizeParkingSpaces(parkingSpaces),
      normalizeParkingSpaces(originalParkingSpaces)
    );

    const relatedLocationsChanged = !isEqual(
      normalizeRelatedLocations(relatedLocations),
      normalizeRelatedLocations(originalRelatedLocations)
    );

    setHasChanges(
      siteChanged ||
        thirdPartyChanged ||
        accessGroupsChanged ||
        partiesChanged ||
        parkingSpacesChanged ||
        relatedLocationsChanged
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [
    chargingSite,
    thirdPartySystemMapping,
    accessGroupPublicIds,
    parties,
    originalChargingSite,
    originalThirdPartySystemMapping,
    originalAccessGroupPublicIds,
    originalParties,
    parkingSpaces,
    originalParkingSpaces,
    relatedLocations,
    originalRelatedLocations,
  ]);

  const updateSite = async () => {
    const invalidVtIndices = parkingSpaces.reduce((acc, space, i) => {
      const vt = space.vehicleTypes;
      if (
        !Array.isArray(vt) ||
        vt.length === 0 ||
        vt.some((v) => !String(v ?? "").trim())
      ) {
        acc.push(i);
      }
      return acc;
    }, []);

    const invalidPhysicalRefIndices = parkingSpaces.reduce((acc, space, i) => {
      if (!String(space?.physicalReference ?? "").trim()) {
        acc.push(i);
      }
      return acc;
    }, []);

    if (invalidVtIndices.length > 0 || invalidPhysicalRefIndices.length > 0) {
      setInvalidParkingVehicleTypesIndices(invalidVtIndices);
      setInvalidParkingPhysicalReferenceIndices(invalidPhysicalRefIndices);
      setActiveTab("data");

      if (
        invalidVtIndices.length > 0 &&
        invalidPhysicalRefIndices.length > 0
      ) {
        toast.error(t("site.parkingNeedsRefAndType"));
      } else if (invalidPhysicalRefIndices.length > 0) {
        toast.error(t("site.parkingNeedsRef"));
      } else {
        toast.error(t("site.parkingNeedsType"));
      }
      return;
    }

    setInvalidParkingVehicleTypesIndices([]);
    setInvalidParkingPhysicalReferenceIndices([]);

    const toastId = toast.loading(t("toast.updating", { entity: t("entity.chargingSite") }));
    try {
      const accessToken = await getAccessToken();

      const dto = toUpdateChargingSiteDto(chargingSite);

      if (!dto.chargingSitePublicId) {
        toast.error(t("toast.failedUpdate", { entity: t("entity.chargingSite") }), { id: toastId });
        return;
      }

      if (
        thirdPartySystemMapping.length === 0 &&
        originalThirdPartySystemMapping.length > 0
      ) {
        dto.thirdPartyMappings = originalThirdPartySystemMapping;
      } else {
        dto.thirdPartyMappings = thirdPartySystemMapping.filter(
          (thirdPartySystem) =>
            thirdPartySystem.isNew || !thirdPartySystem.isDeleted
        );
      }

      dto.accessGroupPublicIds = accessGroupPublicIds;

      dto.remotePartyPublicIds = parties.filter((p) => !p.isDeleted).map(
        (p) => p.remotePartyPublicId
      );

      dto.openingHours = openingTimes;
      dto.parkingSpaces = parkingSpaces.map(toParkingSpaceDto);
      dto.relatedLocations = relatedLocations.map(toRelatedLocationDto);

      await updateChargingSite(dto, accessToken);

      toast.success(t("toast.updated", { entity: t("entity.chargingSite") }), { id: toastId });

      setOriginalThirdPartySystemMapping(thirdPartySystemMapping);
      setOriginalChargingSite(normalizeSite(chargingSite));
      setOriginalAccessGroupPublicIds(accessGroupPublicIds);
      setOriginalParties(parties);
      setOriginalParkingSpaces(parkingSpaces);
      setOriginalRelatedLocations(relatedLocations);
      setHasChanges(false);
    } catch (error) {
      console.error(
        "Error response from API:",
        error.response ? error.response.data : error
      );
      toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.chargingSite") }), {
        id: toastId,
      });
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: t("confirm.discard"),
        confirm: () => {
          navigate("/sites");
        },
      }));
    } else {
      navigate("/sites");
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const accessToken = await getAccessToken();
      const siteOpeningHoursData = await getSiteOpeningHours(id, accessToken);

      if (siteOpeningHoursData) {
        setOpeningTimes(siteOpeningHoursData);
      }
    } catch (error) {
      toast.error(
        error.message || t("toast.failedFetch", { entity: t("entity.chargingSite") })
      );
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={chargingSite.name} loading={loadingSite} />
          </div>

          <div className={`${loadingSite ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex ">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">
                {id !== "0" ? chargingSite.name : t("pages.createSite")}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!chargingSite?.name || !hasChanges}
                  onClick={updateSite}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg rounded-md w-full ">
              <div className="border-b border-mute-200 mb-4 gap-4 flex">
                {TAB_ITEMS.filter((t) => (t.id === "references" ? isAdmin : true)).map((tab) => (
                  <button
                    key={tab.id}
                    className={getTabButtonClassName(activeTab, tab.id)}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {t(tab.labelKey)}
                  </button>
                ))}
              </div>

              {activeTab === "details" && (
                <>
                  <SiteForm
                    chargingSite={chargingSite}
                    setChargingSite={setChargingSite}
                    setHasChanges={setHasChanges}
                    heavyVehicleData={heavyVehicleData}
                    setHeavyVehicleData={setHeavyVehicleData}
                    isEdit={true}
                    handleInputChange={handleInputChange}
                  />
                  <SiteGridSettings
                    chargingSite={chargingSite}
                    setChargingSite={setChargingSite}
                    setHasChanges={setHasChanges}
                  />
                </>
              )}

              {activeTab === "hierarchy" && (
                <SiteHierarchy
                  chargingSite={chargingSite}
                  resyncChargingSite={fetchChargingSite}
                />
              )}

              {isAdmin && activeTab === "references" && (
                <SiteThirdPartyRef
                  hasChanges={hasChanges}
                  thirdPartySystemMapping={thirdPartySystemMapping}
                  setThirdPartySystemMapping={setThirdPartySystemMapping}
                  setHasChanges={setHasChanges}
                />
              )}

              {activeTab === "accessGroups" && (
                <EntityMultiSelectForm
                  key={`${id}-${chargingSite?.operatorBusinessPublicId ?? "site"}`}
                  reloadKey={`${isAdmin}-${chargingSite?.operatorBusinessPublicId ?? "cpms"}-${siteAccessGroupItems.length}`}
                  title={t("entity.accessGroups")}
                  fetchItems={async (token) => {
                    const operatorOrganizationPublicId =
                      chargingSite?.operatorBusinessPublicId ?? null;

                    let fetched = [];
                    if (isAdmin && operatorOrganizationPublicId) {
                      fetched = await getSimpleAccessGroupsByOperatorOrganizationPublicId(
                        operatorOrganizationPublicId,
                        token
                      );
                    } else {
                      fetched = await getAllAccessGroups(token);
                    }

                    return mergeAccessGroupOptions(
                      includeOrganization,
                      siteAccessGroupItems,
                      fetched || []
                    );
                  }}
                  labelKey="displayName"
                  valueKey="accessGroupPublicId"
                  selectedIds={accessGroupPublicIds}
                  onChange={setAccessGroupPublicIds}
                />
              )}

              {activeTab === "events" && <ChargingSiteStatusLog />}

              {activeTab === "data" && (
                <div className="flex flex-col gap-8">
                  {openingTimes && (
                    <LocationData
                      openingTimes={openingTimes}
                      setChargingSite={setChargingSite}
                      chargingSite={chargingSite}
                      setOpeningTimes={setOpeningTimes}
                      setHasChanges={setHasChanges}
                      parties={parties}
                      setParties={setParties}
                      parkingSpaces={parkingSpaces}
                      setParkingSpaces={setParkingSpaces}
                      ocpiEditable={isAdmin}
                      invalidParkingVehicleTypesIndices={
                        invalidParkingVehicleTypesIndices
                      }
                      onClearParkingVehicleTypesInvalid={
                        clearParkingVehicleTypesInvalid
                      }
                      invalidParkingPhysicalReferenceIndices={
                        invalidParkingPhysicalReferenceIndices
                      }
                      onClearParkingPhysicalReferenceInvalid={
                        clearParkingPhysicalReferenceInvalid
                      }
                      relatedLocations={relatedLocations}
                      setRelatedLocations={setRelatedLocations}
                      heavyVehicleData={heavyVehicleData}
                      setHeavyVehicleData={setHeavyVehicleData}
                    />
                  )}
                </div>
              )}

              {activeTab === "smartCharging" && (
                <>
                  <SiteSmartCharging chargingSite={chargingSite} />
                  <PointSmartCharging chargingSite={chargingSite} />
                </>
              )}
            </div>
          </div>
        </main>

        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
          onConfirm={() => {
            setConfirmState({ ...confirmState, isOpen: false });
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditSitePage;
